import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@purinanbm/pds-ui';
import React, { useContext } from 'react';
import { Spinner } from 'react-bootstrap';
import { getActiveFacetCount } from '../../utils/helpers';
import FacetItem from '../facets/FacetItem';
import { SearchListingContext } from './SearchListingContext';

interface FacetProps {
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const SearchFacets: React.FC<FacetProps> = function (props) {
  const context = useContext(SearchListingContext);

  if (!context) {
    throw new Error(
      'Child components of SearchListing cannot be rendered outside the SearchListing component!',
    );
  }

  const { id, data, loaded, containerRef } = context || {};
  const { facets } = data || {};

  if (!loaded) {
    return (
      <>
        <span aria-live="polite" className="visually-hidden">
          Loading...
        </span>
        <Spinner animation="border" role="status" />
      </>
    );
  }

  if (!facets) {
    return null;
  }

  const getActiveAccordions = () => {
    const facetsUsed = Object.values(facets);

    // it will default to those values initially only if they truly exist within the facets
    const activeAccordions: string[] = [
      `${id}-facets-field_category`,
      `${id}-facets-field_species_term`,
    ];

    facetsUsed.forEach(facet => {
      const found = (facet?.facets || []).some(item => item.values.active);
      if (found) {
        activeAccordions.push(`${id}-facets-${facet.field_id}`);
      }
    });

    return activeAccordions;
  };

  return (
    <Accordion
      defaultValue={getActiveAccordions()}
      id={`${id}-facets`}
      type="multiple"
      options={{ headingLevel: 'h3' }}
    >
      {Object.values(facets).map((facet: any) => {
        if (facet?.facets?.length > 0) {
          const activeCount = getActiveFacetCount(facet);
          return (
            <AccordionItem
              key={`${id}-facets-${facet.field_id}`}
              value={`${id}-facets-${facet.field_id}`}
            >
              <div id={`${id}-facets-${facet.field_id}`} className="[&_h2]:pds-mb-0">
                <AccordionTrigger className="pds-group/trigger pds-p-4 pds-text-body-md pds-font-bold data-[state=open]:pds-bg-surface-layer">
                  {facet.label}
                  {activeCount > 0 && (
                    <span className="pds-ml-auto pds-inline-flex pds-h-5 pds-w-5 pds-items-center pds-justify-center pds-rounded-circle pds-bg-surface-layer group-data-[state=open]/trigger:pds-bg-surface">
                      {activeCount}{' '}
                      <span className="pds-sr-only">
                        {activeCount > 1 ? 'filters selected' : 'filter selected'}
                      </span>
                    </span>
                  )}
                </AccordionTrigger>
              </div>

              <AccordionContent className="pds-p-4" role="group">
                <ul className="pds-space-y-3.5 pds-pl-0">
                  {facet.facets.map((facetItem: FacetLink, i: number) => {
                    if (facetItem && id) {
                      return (
                        <li className="pds-list-none" key={`${id}-facets-${facet.field_id}-${i}`}>
                          <FacetItem
                            id={`${id}-facets-${facet.field_id}`}
                            parentFacet={facet}
                            onChange={e => {
                              props.onChange(e);
                              containerRef.current?.scrollIntoView({ behavior: 'smooth' });
                            }}
                            facet={facetItem}
                          />
                        </li>
                      );
                    }
                    return null;
                  })}
                </ul>
              </AccordionContent>
            </AccordionItem>
          );
        }
        return null;
      })}
    </Accordion>
  );
};

SearchFacets.displayName = 'SearchListing.SearchFacets';

export default SearchFacets;
